import { CampaignsFactory, DialogHandler, EStorageType, fetchRoute, FormsFactory, GenericWrapperContext, getBrandUrl, getNestedProperty, getStorage, Header, Icon, ListsFactory, Search, SendersFactory, setStorage, SideMenu, SideMenuContainer, SubNav, SupportFactory, TemplatesFactory } from "@cakemail-org/ui-components-v2"
import { TSubNavItem } from "@cakemail-org/ui-components-v2/dist/esm/components/subNav/types"
import { MouseEvent, useContext, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { ERoutes } from "../../utils/url"
import "./styles.scss"
import { MenuSettings } from "./MenuSettings"
import { TAppMenu, TAppMenuSearchResultItem } from "./types"
import { Stack } from "@mui/material"
import { connectToZendeskSupport } from "../../utils/api"
import { EPageCustomization, hasPageElementAccess } from "../../utils/limits_features"
import { goToBilling } from "../../utils/billing"
import { getMenuAdd, getMenuHome, getMenuSettings, getMenuSubNav } from "./menuData"
import { getBaseBeeTemplate } from "../../data/bee"
import { showCreateTemplateDialog } from "../dialogs/templateCreate"
import { showCreateAccountDialog } from "../dialogs/accountCreate"
import { showChangeLanguageDialog } from "../dialogs/languageChange"

export function AppMenu({ onLogout, setUser, showTagLibraryDialog }: TAppMenu) {
    const genericWrapperContext = useContext(GenericWrapperContext)
    const [nav, setNav] = useState<string>("home") // home, add, search
    const { t } = useTranslation()
    const [search, setSearch] = useState<string>("")
    const [searchedResources, setSearchedResources] = useState<TAppMenuSearchResultItem[]>([])
    const [loading, setLoading] = useState<boolean>(false)
    const [settingView, setSettingView] = useState<boolean>(false)
    const [recentlySearched, setRecentlySearched] = useState<TAppMenuSearchResultItem[]>([])

    const { account, partnerBrand, user } = genericWrapperContext

    useEffect(() => {
        if (search) {
            fetchSearchableResources()
        } else {
            setSearchedResources([])
        }
    }, [search])

    useEffect(() => {
        const recentlySearched = getStorage({ key: "menuRecentlySearched", type: EStorageType.local })
        if (recentlySearched?.length > 0) {
            setRecentlySearched(recentlySearched)
        }
    }, [])

    function fetchSearchableResources() {
        setLoading(true)
        Promise.allSettled([
            CampaignsFactory.list({
                filter: "name==*" + search + "*",
                page: 1,
                perPage: 25
            }),
            TemplatesFactory.list({
                filter: "name==*" + search + "*",
                page: 1,
                perPage: 25
            }),
            FormsFactory.list({
                filter: "name==*" + search + "*",
                page: 1,
                perPage: 25
            }),
            ListsFactory.list({
                filter: "name==*" + search + "*",
                page: 1,
                perPage: 25
            }),

            /*AutomationsFactory.list({
                filter: "name==*" + search + "*",
                page: 1,
                perPage: 25
            })*/
        ]).then(([campaigns, templates, forms, lists]) => {
            let items: TAppMenuSearchResultItem[] = []

            if (campaigns.status === "fulfilled") {
                items = items.concat(campaigns.value.data.map((campaign) => {
                    let status: any = campaign.status
                    if (status === "incomplete") status = t("general-email-incomplete")
                    else if (status === "delivering") status = t("general-email-delivering")
                    else if (status === "delivered") status = t("general-campaign-report")
                    else if (status === "scheduled") status = t("general-email-scheduled")
                    else status = t("general-email-campaign")

                    return {
                        name: campaign.name + "-" + campaign.id,
                        text: campaign.name,
                        support: status,
                        path: "#" + fetchRoute({ route: status === "delivered" ? ERoutes.campaignReport : ERoutes.campaignEdit, params: { campaignId: campaign.id.toString() } }),
                        defaultItemIcon: false,
                    }
                }))
            }

            if (templates.status === "fulfilled") {
                items = items.concat(templates.value.data.map((template) => {
                    return {
                        name: template.name + "-" + template.id,
                        text: template.name,
                        support: t("general-email-template_one"),
                        path: "#" + fetchRoute({ route: ERoutes.templateEdit, params: { templateId: template.id.toString() } }),
                        defaultItemIcon: false,
                    }
                }))
            }

            if (forms.status === "fulfilled") {
                items = items.concat(forms.value.data.map((form) => {
                    return {
                        name: form.name + "-" + form.id,
                        text: form.name,
                        support: t("general-form_one"),
                        path: "#" + fetchRoute({ route: ERoutes.forms, params: { formId: form.id.toString() } }),
                        defaultItemIcon: false,
                    }
                }))
            }

            if (lists.status === "fulfilled") {
                items = items.concat(lists.value.data.map((list) => {
                    return {
                        name: list.name + "-" + list.id,
                        text: list.name,
                        support: t("general-list_one"),
                        path: "#" + fetchRoute({ route: ERoutes.lists }) + "?listId=" + list.id.toString(),
                        defaultItemIcon: false,
                    }
                }))
            }

            setSearchedResources(items)
            setLoading(false)
        })
    }

    function handleSearchItemClick(event: MouseEvent<HTMLAnchorElement>) {
        const name = event.currentTarget.getAttribute("data-name")
        const foundItem = searchedResources.find((item) => item.name === name)

        if (foundItem) {
            const newRecentlySearched = [foundItem, ...recentlySearched]

            if (newRecentlySearched.length > 10) {
                newRecentlySearched.pop()
            }

            setRecentlySearched(newRecentlySearched)
            setStorage({ key: "menuRecentlySearched", data: newRecentlySearched, type: EStorageType.local })
        }
    }


    function handleCreateMenuClick(event: MouseEvent<HTMLAnchorElement>) {
        const name = event.currentTarget.getAttribute("data-name")
        if (name === "campaignCreate") {
            if (hasPageElementAccess({ pageElement: EPageCustomization.campaignCreateGoesToOverview, partnerBrand })) {
                event.preventDefault()
                CampaignsFactory.create({
                    campaign: {
                        name: t("general-untitled"),
                        content: {
                            type: "bee",
                            json: getBaseBeeTemplate()
                        }
                    }
                }).then((data) => {
                    window.location.href = "#" + fetchRoute({ route: ERoutes.campaignEdit, params: { campaignId: data.id.toString() } })
                }).catch((error) => {
                    DialogHandler.show({
                        type: "error",
                        title: t("campaign-create-error"),
                    })
                })


            }
        } else if (name === "createTemplate") {
            event.preventDefault()
            showCreateTemplateDialog()
        } else if (name === "createAccount") {
            event.preventDefault()
            showCreateAccountDialog()
        } else if (name === "createTag") {
            event.preventDefault()
            alert("createTag, not implemented, would open an overlay")
        }
    }

    function handleNavClick(event: MouseEvent<HTMLButtonElement>, item: TSubNavItem) {
        setNav(item.name)
    }

    function handleSearchChange(cSearch: string) {
        setSearch(cSearch)
    }

    function handleToggleSettingView() {
        setSettingView(!settingView)
    }

    function handleHelpClick() {
        connectToZendeskSupport(genericWrapperContext)
    }



    return <SideMenu
        className="appMenu"
        logoUrl={getBrandUrl({ brand: partnerBrand, elKey: "logo" })}
        canMinify
        onMouseLeave={() => {
            setSettingView(false)
            setNav("home")
        }}
    >

        {settingView &&
            <>
                <Stack direction="row" spacing={2} alignItems="center" >
                    <Icon name="Arrow18" onClick={handleToggleSettingView} sx={{ cursor: "pointer" }} className="hoverAble" />
                    <Header
                        heading={t("general-setting_other")}
                    />
                </Stack>

                <SideMenuContainer
                    menuItemClick={(event) => {
                        const name = event.currentTarget.getAttribute("data-name")
                        if (name === "help") {
                            event.preventDefault()
                            handleHelpClick()
                        } else if (name === "plans&billing") {
                            event.preventDefault()
                            goToBilling({ account, user, brand: partnerBrand })
                        } else if (name === "changeLanguage") {
                            event.preventDefault()
                            showChangeLanguageDialog(setUser)
                        } else if (name === "tagLibrary") {
                            showTagLibraryDialog && showTagLibraryDialog()
                        }
                    }}
                    menuItems={getMenuSettings({ account, partnerBrand, user })}
                />
            </>
        }

        {!settingView &&
            <>
                <SubNav
                    selected={nav}
                    menuVariant='inherit'
                    btnOverride={{
                        color: "secondary",
                        variant: "contained"
                    }}
                    onClick={handleNavClick}
                    items={getMenuSubNav()}
                />

                {nav === "home" &&
                    <SideMenuContainer
                        menuItems={getMenuHome({ account, partnerBrand })}
                    />
                }
                {nav === "add" &&
                    <SideMenuContainer
                        menuItemClick={handleCreateMenuClick}
                        menuItems={getMenuAdd({ account, partnerBrand, user })}
                    />
                }

                {nav === "search" &&
                    <>
                        <Search
                            onChange={handleSearchChange}
                            placeholder={t("general-search-resources-placeholder")}
                            initValue={search}
                            className={loading ? "loading" : ""}
                        />
                        <SideMenuContainer
                            className={loading ? "loading" : ""}
                            forcedLocationPath="/campaigns/draft"
                            menuItems={search ? searchedResources : [{
                                text: t("general-recently-searched"),
                                name: "recentlySearched",
                                variant: "subSection",
                                defaultItemIcon: false,
                            }, ...recentlySearched
                            ]}
                            menuItemClick={handleSearchItemClick}
                        />
                    </>
                }
            </>
        }

        <MenuSettings onGearClick={handleToggleSettingView} onLogout={onLogout} />

    </SideMenu >
}